/**
 * Centralized error handling utilities for the hangout system
 * Provides retry mechanisms for network operations and graceful failure handling
 */

const DEFAULT_RETRYABLE_PATTERNS = [
    /failed to fetch/i,
    /network(?:\s+request)?\s+failed/i,
    /network\s+error/i,
    /timeout/i,
    /\beco(?:nn)?(?:reset|naborted|nrefused)\b/i
];

const DEFAULT_RETRYABLE_STATUS_CODES = [408, 429, 502, 503, 504];
const SAFE_RETRY_METHODS = new Set(['GET', 'HEAD', 'OPTIONS']);
const RETRY_TELEMETRY_EVENT = 'request:retry';

/**
 * @typedef RetryTelemetryPayload
 * @property {string} [event_type]
 * @property {string | null} [endpoint]
 * @property {string | null} [method]
 * @property {number} [attempt]
 * @property {number} [max_attempts]
 * @property {number | null} [final_status]
 * @property {string | null} [outcome]
 * @property {number} [retry_delay_ms]
 * @property {number} [retries_used]
 * @property {string | null} [error_name]
 * @property {string | null} [error_message]
 */

/**
 * @typedef RetryConfig
 * @property {number} [maxRetries]
 * @property {number} [retryDelay]
 * @property {number} [backoffMultiplier]
 * @property {number} [jitterRatio]
 * @property {string[]} [retryableErrors]
 * @property {number[]} [retryableStatusCodes]
 * @property {RegExp[]} [retryablePatterns]
 * @property {boolean} [allowUnsafeMethods]
 * @property {boolean} [logOnly]
 * @property {string} [method]
 * @property {string} [endpoint]
 * @property {(payload: RetryTelemetryPayload) => void} [onRetryEvent]
 */

/**
 * Configuration for different types of operations
 */
const ERROR_CONFIG = {
    NETWORK: {
        maxRetries: 2,
        retryDelay: 600,
        backoffMultiplier: 2,
        jitterRatio: 0.25,
        retryableErrors: ['NetworkError', 'TimeoutError', 'TypeError'],
        retryableStatusCodes: DEFAULT_RETRYABLE_STATUS_CODES,
        retryablePatterns: DEFAULT_RETRYABLE_PATTERNS
    },
    CRITICAL_INIT: {
        maxRetries: 2,
        retryDelay: 500,
        backoffMultiplier: 2,
        jitterRatio: 0.2,
        showUserPrompt: true,
        retryableErrors: ['NetworkError', 'TimeoutError', 'TypeError'],
        retryableStatusCodes: DEFAULT_RETRYABLE_STATUS_CODES,
        retryablePatterns: DEFAULT_RETRYABLE_PATTERNS
    },
    NON_CRITICAL: {
        maxRetries: 1,
        retryDelay: 200,
        backoffMultiplier: 1.5,
        jitterRatio: 0.15,
        logOnly: true,
        retryableErrors: ['NetworkError', 'TimeoutError', 'TypeError'],
        retryableStatusCodes: DEFAULT_RETRYABLE_STATUS_CODES,
        retryablePatterns: DEFAULT_RETRYABLE_PATTERNS
    }
};

/**
 * Sleep utility for retry delays
 */
const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms));

/**
 * Build a retryable HTTP error to integrate status-based retry with fetch.
 */
export const createRetryableHttpError = (response, metadata = {}) => {
    const status = Number(response?.status);
    const statusText = response?.statusText || 'Unknown';
    const method = String(metadata.method || 'GET').toUpperCase();
    const endpoint = String(metadata.endpoint || metadata.url || '');

    const error = /** @type {any} */ (new Error(`Retryable HTTP status ${status}: ${statusText}`));
    error.name = 'RetryableHttpError';
    error.status = Number.isFinite(status) ? status : null;
    error.statusText = statusText;
    error.method = method;
    error.endpoint = endpoint;
    error.isRetryable = true;
    return error;
};

/**
 * Returns true when a request method should be retried by default.
 */
export const shouldRetryMethod = (method, allowUnsafeMethods = false) => {
    if (allowUnsafeMethods) return true;
    if (!method) return true;
    return SAFE_RETRY_METHODS.has(String(method).toUpperCase());
};

/**
 * @param {string | {label?: string, name?: string, endpoint?: string, url?: string, method?: string}} [context]
 * @param {RetryConfig} [config]
 */
const normalizeRetryContext = (context = '', config = {}) => {
    const resolvedContext = /** @type {any} */ (context);
    if (typeof resolvedContext === 'string') {
        return {
            label: resolvedContext || 'Operation',
            endpoint: config.endpoint || '',
            method: config.method ? String(config.method).toUpperCase() : ''
        };
    }

    return {
        label: resolvedContext.label || resolvedContext.name || 'Operation',
        endpoint: resolvedContext.endpoint || resolvedContext.url || '',
        method: resolvedContext.method ? String(resolvedContext.method).toUpperCase() : ''
    };
};

const extractStatusCode = (error) => {
    const directStatus = Number(error?.status);
    if (Number.isFinite(directStatus)) return directStatus;

    const responseStatus = Number(error?.response?.status);
    return Number.isFinite(responseStatus) ? responseStatus : null;
};

export const isTransientHttpStatus = (statusCode, retryableStatusCodes = DEFAULT_RETRYABLE_STATUS_CODES) => {
    if (!Number.isFinite(Number(statusCode))) return false;
    return retryableStatusCodes.includes(Number(statusCode));
};

/**
 * Check if an error is retryable based on type, message, and optional status.
 */
const isRetryableError = (error, {
    retryableErrors = [],
    retryablePatterns = DEFAULT_RETRYABLE_PATTERNS,
    retryableStatusCodes = DEFAULT_RETRYABLE_STATUS_CODES
} = {}) => {
    if (!error) return false;
    if (error.name === 'AbortError') return false;
    if (error.isRetryable === true) return true;

    const statusCode = extractStatusCode(error);
    if (isTransientHttpStatus(statusCode, retryableStatusCodes)) return true;

    const errorName = String(error.name || error.constructor?.name || '');
    const errorMessage = String(error.message || '');
    const hasRetryableName = retryableErrors.some((retryableError) => (
        errorName.includes(retryableError) || errorMessage.includes(retryableError)
    ));
    if (hasRetryableName) return true;

    return retryablePatterns.some((pattern) => pattern.test(errorMessage));
};

const getRetryDelayWithJitter = (baseDelay, jitterRatio = 0) => {
    if (baseDelay <= 0) return 0;
    const jitterRange = Math.max(0, baseDelay * jitterRatio);
    const randomOffset = jitterRange > 0 ? (Math.random() * 2 * jitterRange) - jitterRange : 0;
    return Math.max(0, Math.round(baseDelay + randomOffset));
};

/**
 * @param {RetryConfig} config
 * @param {RetryTelemetryPayload} payload
 */
const emitRetryTelemetry = (config, payload) => {
    if (typeof config.onRetryEvent === 'function') {
        try {
            config.onRetryEvent(payload);
        } catch (telemetryError) {
            console.warn('Retry telemetry callback failed:', telemetryError);
        }
    }

    if (typeof document !== 'undefined' && typeof document.dispatchEvent === 'function') {
        document.dispatchEvent(new CustomEvent(RETRY_TELEMETRY_EVENT, { detail: payload }));
    }
};

/**
 * Generic retry wrapper for any async operation.
 */
/**
 * @param {() => Promise<any>} operation
 * @param {RetryConfig} [config]
 * @param {string | {label?: string, name?: string, endpoint?: string, url?: string, method?: string}} [context]
 */
export const withRetry = async (operation, config = ERROR_CONFIG.NETWORK, context = '') => {
    const retryConfig = /** @type {RetryConfig} */ ({ ...ERROR_CONFIG.NETWORK, ...(config || {}) });
    const {
        maxRetries = 0,
        retryDelay = 0,
        backoffMultiplier = 1,
        jitterRatio = 0,
        retryableErrors = [],
        retryableStatusCodes = DEFAULT_RETRYABLE_STATUS_CODES,
        retryablePatterns = DEFAULT_RETRYABLE_PATTERNS,
        allowUnsafeMethods = false
    } = retryConfig;

    const retryContext = normalizeRetryContext(context, retryConfig);
    const canRetryMethod = shouldRetryMethod(retryContext.method || retryConfig.method, allowUnsafeMethods);
    const effectiveMaxRetries = canRetryMethod ? maxRetries : 0;
    const maxAttempts = effectiveMaxRetries + 1;
    let lastError;
    let currentDelay = retryDelay;

    for (let attempt = 0; attempt < maxAttempts; attempt++) {
        try {
            const result = await operation();
            if (attempt > 0) {
                console.log(`✅ ${retryContext.label} succeeded after ${attempt} retries`);
            }
            emitRetryTelemetry(retryConfig, {
                event_type: 'request_retry_success',
                endpoint: retryContext.endpoint || null,
                method: retryContext.method || null,
                attempt: attempt + 1,
                max_attempts: maxAttempts,
                retries_used: attempt,
                final_status: null,
                outcome: 'success'
            });
            return result;
        } catch (error) {
            const resolvedError = /** @type {any} */ (error);
            lastError = resolvedError;
            const statusCode = extractStatusCode(resolvedError);
            const isRetryable = isRetryableError(resolvedError, {
                retryableErrors,
                retryablePatterns,
                retryableStatusCodes
            });
            const isFinalAttempt = attempt >= effectiveMaxRetries;
            const payloadBase = {
                endpoint: retryContext.endpoint || null,
                method: retryContext.method || null,
                attempt: attempt + 1,
                max_attempts: maxAttempts,
                final_status: statusCode,
                error_name: resolvedError?.name || null,
                error_message: resolvedError?.message || null
            };

            if (!isRetryable || isFinalAttempt) {
                emitRetryTelemetry(retryConfig, {
                    ...payloadBase,
                    event_type: 'request_retry_exhausted',
                    outcome: !isRetryable ? 'non_retryable' : 'exhausted'
                });

                if (!isRetryable) {
                    console.warn(`❌ ${retryContext.label} failed with non-retryable error:`, resolvedError.message);
                }
                break;
            }

            const delayWithJitter = getRetryDelayWithJitter(currentDelay, jitterRatio);
            emitRetryTelemetry(retryConfig, {
                ...payloadBase,
                event_type: 'request_retry_attempt',
                outcome: 'retrying',
                retry_delay_ms: delayWithJitter
            });
            console.warn(`⚠️ ${retryContext.label} failed (attempt ${attempt + 1}/${maxAttempts}):`, resolvedError.message);

            if (delayWithJitter > 0) await sleep(delayWithJitter);
            currentDelay *= backoffMultiplier;
        }
    }

    // All retries exhausted
    if (retryConfig.logOnly) {
        console.error(`❌ ${retryContext.label} failed after ${maxAttempts} attempts:`, lastError);
        return null; // Return null for non-critical operations
    }

    throw lastError;
};

/**
 * Specialized wrapper for network operations (API calls, fetches, etc.)
 */
export const withNetworkRetry = (operation, context = 'Network operation') => {
    return withRetry(operation, ERROR_CONFIG.NETWORK, context);
};

/**
 * Specialized wrapper for critical initialization operations
 */
export const withCriticalRetry = (operation, context = 'Critical operation') => {
    return withRetry(operation, ERROR_CONFIG.CRITICAL_INIT, context);
};

/**
 * Specialized wrapper for non-critical operations that should fail gracefully
 */
export const withGracefulFailure = (operation, context = 'Non-critical operation') => {
    return withRetry(operation, ERROR_CONFIG.NON_CRITICAL, context);
};

/**
 * Global initialization failure handler
 */
export const handleInitializationFailure = async (error, context = 'Initialization') => {
    console.error(`🚨 ${context} failed:`, error);
    
    // Show user-friendly error modal
    const shouldRetry = await showInitializationErrorModal(error, context);
    
    if (shouldRetry) {
        // Reload the page to retry initialization
        window.location.reload();
    } else {
        // Redirect to a safe fallback page
        window.location.href = './rankings.html';
    }
};

/**
 * Create and show an error modal for initialization failures
 */
const showInitializationErrorModal = (error, context) => {
    return new Promise((resolve) => {
        // Create modal HTML
        const modal = document.createElement('div');
        modal.className = 'modal error-modal';
        modal.style.display = 'block';
        modal.style.zIndex = '10000';
        
        modal.innerHTML = `
            <div class="modal-content error-modal-content">
                <div class="error-icon">⚠️</div>
                <h2>Oops! Something went wrong</h2>
                <p>We encountered an issue while loading the hangout system.</p>
                <p class="error-details">Error: ${error.message || 'Unknown error'}</p>
                <div class="error-actions">
                    <button class="btn btn-primary retry-btn">Try Again</button>
                    <button class="btn btn-secondary home-btn">Go Home</button>
                </div>
            </div>
        `;

        document.body.appendChild(modal);

        // Handle button clicks
        modal.querySelector('.retry-btn').addEventListener('click', () => {
            document.body.removeChild(modal);
            resolve(true);
        });

        modal.querySelector('.home-btn').addEventListener('click', () => {
            document.body.removeChild(modal);
            resolve(false);
        });
    });
};

/**
 * Simple error logger for non-critical failures
 */
export const logError = (error, context = '') => {
    console.warn(`⚠️ ${context}:`, error.message || error);
};


